<template>
  <section>

    <template v-if="canSharingBeDisabled">
      <u-form-group label="Share Link" size="xl" :ui="{wrapper: 'flex justify-between'}">
        <core-confirm-popover :confirm-method="updateSharing" confirm-text="Confirming will permanently delete the current share link. Continue?" :confirm-when="isSharingEnabled">
          <u-toggle v-model="isSharingEnabled" class="pointer-events-none" />
        </core-confirm-popover>
      </u-form-group>
      <p class="text-sm text-charcoal-400">Anyone on the internet with the link can view and download this content.</p>
    </template>

    <p v-else-if="shareType === CORE_SHARE_TYPES.profile">This link enables you to share your FOREVER profile with anyone.</p>
    <p v-else-if="shareType === CORE_SHARE_TYPES.digiProject || shareType === CORE_SHARE_TYPES.digiFile">Anyone on the internet with the link can view and download this content. <b class="text-salmon">This link expires on {{$dayjs(shareObject.download_expires_at).format('l')}}.</b></p>
    <p v-else class="text-sm text-charcoal-400">Anyone on the internet with the link can view and download this content.</p>

    <transition name="f-fade">
      <div v-if="isSharingEnabled" class="mt-4">

        <core-contextual-loading-box class="inline-block" v-if="isGettingUrl" loading-text="" size="sm" />

        <div v-else class="flex gap-1.5 sm:gap-3 items-start flex-wrap">

          <core-copy-button
            :text="shareLink"
            data-gtmid="core_ui-common-share_modal_copy"
          />

          <core-qr-code-button
            :url="shareLink"
            :filename="shareObject.name"
            data-gtmid="core_ui-common-share_modal_qrcode"
          />

          <u-button
            variant="outline"
            icon="i-ri-mail-line"
            :to="emailLink"
            target="_blank"
            rel="noopener noreferrer"
            label="Email"
            class="no-underline"
            data-gtmid="core_ui-common-share_modal_email"
          />

          <core-copy-button
            v-if="canEmbed"
            :text="embedMarkup"
            data-gtmid="core_ui-common-share_modal_embed"
            icon="i-ri-code-s-slash-line"
            label="Embed"
            label-copied="Embed Copied!"
          />

        </div>

        <div v-if="isAlbumShare">
          <hr class="border-gray-100 my-4">
          <u-form-group label="Allow anyone with the link to upload" :ui="{wrapper: 'flex justify-between items-center'}">
            <u-checkbox :model-value="isCollaborative" @update:modelValue="updateCollaboration" />
          </u-form-group>

          <p class="text-sm text-charcoal-400 leading-tight mt-1">
            Enables anyone with the link who has an account to upload files to this album. Files uploaded will belong to
            you, appear in your FOREVER Account, and count against your storage.
          </p>
        </div>

      </div>

    </transition>
  </section>
</template>

<script setup>

  const props = defineProps({
    shareType: {
      type: String,
      required: true
    },
    shareObject: {
      type: Object,
      required: true
    }
  });

  const urlShortener = useUrlShortener();
  const isCollaborative = ref(false);
  const isSharingEnabled = ref(true);
  const isUpdatingSharing = ref(false);
  const isUpdateSharingPopoverOpen = ref(false);
  const currentUser = useSessionStore().currentUser;
  const userId = currentUser.slug;
  const isAlbumShare = props.shareType === 'album';
  const canEmbed = props.shareType === 'file' && (props.shareObject.streamable || props.shareObject.is.audio);
  const canSharingBeDisabled = ['album', 'tag', 'file', 'person'].includes(props.shareType);
  const shareObjectId = props.shareObject.id;
  const embedMarkup = computed(() => {
    if (canEmbed) {
      const route = buildRouteParams(SHARE_ROUTES.embed, {
        userId,
        fileId: props.shareObject.id,
        shareToken: props.shareObject.share_token
      });
      const src = `${window.location.origin}${route}`;
      const {width, height} = props.shareObject.is?.audio
          ? {
            width: SHARE_EMBED_FRAME_MAX_VIDEO_DIMENSIONS,
            height: Math.round(SHARE_EMBED_FRAME_MAX_VIDEO_DIMENSIONS * 0.5)
          }
          : (() => {
            const scaleRatio = SHARE_EMBED_FRAME_MAX_VIDEO_DIMENSIONS / Math.max(props.shareObject.width, props.shareObject.height);

            return {
              width: Math.floor(props.shareObject.width * scaleRatio),
              height: Math.floor(props.shareObject.height * scaleRatio)
            };
          })();
      return `<iframe width="${width}" height="${height}" src="${src}" style="border:none;overflow:hidden" allowfullscreen></iframe>`;
    }
  });

  const {data: shareLink, pending: isGettingUrl} = await useAsyncData(
    'shortened-url',
    async () => {
      const base = document.location.origin;
      let route;

      switch (props.shareType) {
        case CORE_SHARE_TYPES.album:
        case CORE_SHARE_TYPES.tag:
        case CORE_SHARE_TYPES.container:
          route = buildRouteParams(SHARE_ROUTES[props.shareType], {
            userId,
            id: props.shareObject.slug || props.shareObject.id, //always fall back on ID since containers do not have slugs
            shareToken: props.shareObject.share_token
          });
          break;
        case CORE_SHARE_TYPES.file:
          route = buildRouteParams(SHARE_ROUTES.file, {
            userId,
            fileId: props.shareObject.id,
            shareToken: props.shareObject.share_token
          });
          break;

        case CORE_SHARE_TYPES.digiProject:
          route = buildRouteParams(SHARE_ROUTES.digiProject, {
            userId,
            projectId: props.shareObject.id,
            shareToken: props.shareObject.share_token
          });
          break;

        case CORE_SHARE_TYPES.digiFile:
          route = buildRouteParams(SHARE_ROUTES.digiFile, {
            userId,
            fileId: props.shareObject.id,
            shareToken: props.shareObject.share_token
          });
          break;
        case CORE_SHARE_TYPES.profile:
          route = buildRouteParams(SHARE_ROUTES.profile, {
            userId
          });
          break;

        case CORE_SHARE_TYPES.person:
          route = buildRouteParams(SHARE_ROUTES[props.shareType], {
            userId,
            id: props.shareObject.id, //use id since slugs can change on people
            shareToken: props.shareObject.share_token
          });
          break;
      }


      try {
        return await urlShortener.getShortenedUrl(`${base}${route}`);
      } catch (e) {
        return `${base}${route}`;
      }
    },
    {watch: [() => props.shareObject.share_token]}
  );

  const emailLink = computed(() => {
    const emailPromoLine = `\n\nFOREVER® makes it easy to organize, store, and share your photos for generations.\nFind out more at https://www.forever.com.`;
    const {name} = useSessionStore().currentUser;
    const query = {
      subject: `${name} shared something great on FOREVER.com`,
      body: props.shareType === CORE_SHARE_TYPES.profile
        ? `${name} shared their profile with you:\n${shareLink.value} ${emailPromoLine}`
        : `${name} shared memories with you:\n${shareLink.value} ${emailPromoLine}`
    };

    return `mailto:${buildQueryString(query)}`;
  });

  watch(() => props.shareObject.collaborative, newVal => (isCollaborative.value = newVal), {immediate: true});
  watch(() => props.shareObject.share_token, newVal => {
    if (canSharingBeDisabled) {
      isSharingEnabled.value = Boolean(newVal);
    }
  }, {immediate: true});

  async function updateCollaboration(value) {
    try {
      isCollaborative.value = value;
      await useAlbumsStore().updateAlbum({id: shareObjectId, updates: {collaborative: value}});
    } catch (e) {
      useErrorToast().add();
      isCollaborative.value = props.shareObject.collaborative;
    }
  }

  async function updateSharing() {
    isUpdateSharingPopoverOpen.value = false;

    const isEnabling = !isSharingEnabled.value;
    const store = (() => {
      switch (props.shareType) {
        case CORE_SHARE_TYPES.file:
          return useFilesStore();
        case CORE_SHARE_TYPES.album:
          return useAlbumsStore();
        case CORE_SHARE_TYPES.tag:
          return useTagsStore();
        case CORE_SHARE_TYPES.person:
          return usePeopleStore();
      }
    })();

    isUpdatingSharing.value = true;

    try {
      store[isEnabling ? 'resetShareToken' : 'disableShareToken']({id: shareObjectId});
    } catch (e) {
      useErrorToast().add();
    } finally {
      isUpdatingSharing.value = false;
    }
  }
</script>
